import { rm } from 'node:fs/promises';
import type { ChildProcess } from 'node:child_process';
import { wirePreviewEnvironmentChildProcess } from '../../process/wire/index.js';
import { sendPreviewEnvironmentManagerStatus } from '../state.js';
import type { PreviewEnvironmentManagerRuntime } from '../runtime.js';
import { detachPreviewEnvironmentProcessIfMatches } from '../stop.js';
import type { StreamTail } from '../stream-tail.js';

export function attachStartedPreviewEnvironment(options: {
  runtime: PreviewEnvironmentManagerRuntime;
  environmentId: string;
  title: string;
  proc: ChildProcess;
  mergedLogPath?: string;
  mergedCleanupDir?: string;
  scheduledGen: number;
  stdoutTail: StreamTail;
  stderrTail: StreamTail;
  scheduleBridgeTailBroadcast: () => void;
}): void {
  const {
    runtime,
    environmentId,
    title,
    proc,
    mergedLogPath,
    mergedCleanupDir,
    scheduledGen,
    stdoutTail,
    stderrTail,
    scheduleBridgeTailBroadcast,
  } = options;
  const { state, getWs, log } = runtime;
  const mergedReadPos = { value: 0 };

  state.processes.set(environmentId, proc);
  wirePreviewEnvironmentChildProcess({
    environmentId,
    title,
    proc,
    mergedLogPath,
    mergedCleanupDir,
    mergedReadPos,
    scheduledGen,
    getSpawnGeneration: () => state.spawnGenerationByServerId.get(environmentId) ?? 0,
    log,
    stdoutTail,
    stderrTail,
    pushRemoteLogChunk: (sid, stream, chunk) => {
      runtime.firehoseSink.pushLogChunk(sid, stream, chunk);
      scheduleBridgeTailBroadcast();
    },
    sendStatus: (status, detail, tails) =>
      sendPreviewEnvironmentManagerStatus(getWs, environmentId, status, detail, tails),
    setPollInterval: (iv) => {
      if (iv) state.mergedLogPollByServerId.set(environmentId, iv);
      else state.mergedLogPollByServerId.delete(environmentId);
    },
    getPollInterval: () => state.mergedLogPollByServerId.get(environmentId),
    isCurrentManagedProcess: () => state.processes.get(environmentId) === proc,
    detachProcessFromManager: () => detachPreviewEnvironmentProcessIfMatches(state, environmentId, proc),
    rmMergedCleanupDir: (dir) => {
      void rm(dir, { recursive: true, force: true }).catch(() => {});
    },
    clearTailBuffers: () => {
      if (state.processes.get(environmentId) !== proc) return;
      state.streamTailsByServerId.delete(environmentId);
    },
  });
  sendPreviewEnvironmentManagerStatus(getWs, environmentId, 'running', `pid=${proc.pid ?? '?'}`, {
    stdout: stdoutTail.getTail(),
    stderr: stderrTail.getTail(),
  });
  runtime.firehoseSink.refreshLogViewers(environmentId);
}
